import React, { Fragment, useState, useEffect } from 'react';
import { useTheme } from '@material-ui/core';
import { useSelector } from 'react-redux';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@material-ui/core';

import DraggableDialog from '../DraggableDialog/DraggableDialog';
import { useTranslation } from 'react-i18next';

const HEADERS = [
  'Density',
  'Nipple',
  "Parenchyma Medial",
  "Parenchyma Lateral",
  "Parenchyma MLO",
  'Pectoralis CC',
  'PNL',
  'Pect. Angle',
  'IMF',
  'Nipple Location',
  'Pect. M. Top',
  'Pect. M. Bottom',
  "Findings BI-RADS 2",
  "Findings BI-RADS 3",
  "Findings BI-RADS 4",
  "Findings BI-RADS 5",
];

const PROJECTIONS = ['rcc', 'lcc', 'rmlo', 'lmlo'];

const LabelingStats = ({ open, onClose }) => {
  const evaluations = useSelector((state) => state.results.evaluations);
  const [mergedEvaluations, setMergedEvaluations] = useState({});
  const theme = useTheme();
  const { t } = useTranslation();

  useEffect(() => {
    if (!evaluations) return;

    const merged = {};

    const sources = [
      evaluations?.density || {},
      evaluations?.quality || {},
      evaluations?.opacities || {},
      evaluations?.microcalc || {},
    ];

    sources.forEach((source) => {
      Object.entries(source).forEach(([evaluator, views]) => {
        if (!merged[evaluator]) {
          merged[evaluator] = {};
        }

        Object.entries(views).forEach(([view, data]) => {
          merged[evaluator][view] = {
            ...merged[evaluator][view],
            ...data,
          };
        });
      });
    });

    setMergedEvaluations(merged);
  }, [evaluations]);



  const warningStyle = {
    background: theme.palette.secondary.dark,
    color: 'black',
  };

  const getLocationString = (location) =>
    Array.isArray(location) ? `${location[0].toFixed(3)}, ${location[1].toFixed(3)}` : '';

  const getCell = (header, result, bbox) => {
    if (!result || !bbox) return { value: '', style: {} };

    const compare = (a, b) => JSON.stringify(a) === JSON.stringify(b);

    try {
      switch (header) {
        case 'Density':
          return {
            value: result.DensClass || bbox.DensClass,
            style: compare(result.DensClass ? result.DensClass : bbox.DensClass , bbox.DensClass) ? {} : warningStyle,
          };
        case 'Nipple':
          return {
            value: result.Nipple || bbox.Nipple,
            style: compare(result.Nipple , bbox.Nipple) ? {} : warningStyle,
          };
        case 'Parenchyma Medial':
          return {
            value: result.ParenchymaCutsMedial || bbox.ParenchymaCutsMedial,
            style: compare(result.ParenchymaCutsMedial , bbox.ParenchymaCutsMedial) ? {} : warningStyle,
          };
        case 'Parenchyma Lateral':
          return {
            value: result.ParenchymaCutsLateral || bbox.ParenchymaCutsLateral,
            style: compare(result.ParenchymaCutsLateral , bbox.ParenchymaCutsLateral) ? {} : warningStyle,
          };
        case 'Parenchyma MLO':
        return {
          value: result.ParenchymaCuts || bbox.ParenchymaCuts,
          style: compare(result.ParenchymaCuts, bbox.ParenchymaCuts) ? {} : warningStyle,
        };
        case 'Pectoralis CC':
          return {
            value: result.PectoralisCC || bbox.PectoralisCC,
            style: compare(result.PectoralisCC , bbox.PectoralisCC) ? {} : warningStyle,
          };
        case 'PNL':
          return {
            value: result.PectoNippleLength ? `${result.PectoNippleLength?.toFixed(2)} mm` : `${bbox.PectoNippleLength?.toFixed(2)} mm`,
            style: result.PectoNippleLength === bbox.PectoNippleLength ? {} : warningStyle,
          };
        case 'Pect. Angle':
          return {
            value: result.PectAngle || bbox.PectAngle,
            style: compare(result.PectAngle, bbox.PectAngle) ? {} : warningStyle,
          };
        case 'IMF':
          return {
            value: result.Infram || bbox.Infram,
            style: compare(result.Infram , bbox.Infram) ? {} : warningStyle,
          };
        case 'Nipple Location':
          return {
            value: getLocationString(result.location_nipple) || getLocationString(bbox.location_nipple),
            style: compare(result.location_nipple, bbox.location_nipple) ? {} : warningStyle,
          };
        case 'Pect. M. Top':
          return result.hasOwnProperty('location_pecto_top')
            ? {
                value: getLocationString(result.location_pecto_top) || getLocationString(bbox.location_pecto_top),
                style: compare(result.location_pecto_top, bbox.location_pecto_top) ? {} : warningStyle,
              }
            : { value: '', style: {} };
        case 'Pect. M. Bottom':
          return result.hasOwnProperty('location_pecto_bottom')
            ? {
                value: getLocationString(result.location_pecto_bottom) || getLocationString(bbox.location_pecto_bottom),
                style: compare(result.location_pecto_bottom, bbox.location_pecto_bottom) ? {} : warningStyle,
              }
            : { value: '', style: {} };
        case 'Findings BI-RADS 2':
          return {
            value: result.birads2 ? 'present' : 'none',
            style: compare(result.birads2, bbox.birads2)
              ? {}
              : warningStyle,
          };
        case 'Findings BI-RADS 3':
          return {
            value: result.birads3 ? 'present' : 'none',
            style: compare(result.birads3, bbox.birads3)
              ? {}
              : warningStyle,
          };
        case 'Findings BI-RADS 4':
          return {
            value: result.birads4 ? 'present' : 'none',
            style: compare(result.birads4, bbox.birads4)
              ? {}
              : warningStyle,
          };
        case 'Findings BI-RADS 5':
          return {
            value: result.birads5 ? 'present' : 'none',
            style: compare(result.birads5, bbox.birads5)
              ? {}
              : warningStyle,
          };
        default:
          return { value: '', style: {} };
      }
    } catch (err) {
      console.warn('Error processing header:', header, err);
      return { value: '', style: {} };
    }
  };

  const renderTableRow = (username, projection, isFirstRow) => {
    const result = mergedEvaluations[username]?.[projection];
    const bbox = mergedEvaluations.bbox?.[projection];

    if (!result && !bbox) return null;

    return (
      <TableRow key={`${username}-${projection}`}>
        <TableCell style={isFirstRow ? { fontWeight: 'bold' } : {}}>{isFirstRow ? username : ''}</TableCell>
        <TableCell>{t(`Dashboard.${projection}`).toUpperCase()}</TableCell>
        {HEADERS.map((header) => {
          const cell = getCell(header, result, bbox);
          return (
            <TableCell key={`${header}-${projection}`} style={cell.style}>
              {cell.value}
            </TableCell>
          );
        })}
      </TableRow>
    );
  };

  return (
    <DraggableDialog
      title={t('ResultView.labelingStatistics')}
      open={open}
      onClose={onClose}
      actions={[{ onClick: onClose, color: 'primary', label: t('ResultView.close') }]}
      maxWidth="xl"
    >
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="labeling table">
          <TableHead style={{ backgroundColor: 'rgb(83 82 82)' }}>
            <TableRow>
              <TableCell style={{ fontWeight: 'bold' }}>{t('ResultView.createdBy')}</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>{t('ResultView.projection')}</TableCell>
              {HEADERS.map((header) => (
                <TableCell key={header} style={{ fontWeight: 'bold' }}>
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <>
            <Fragment key="bbox">
              {PROJECTIONS.map((proj, index) =>
                renderTableRow("bbox", proj, index === 0)
              )}
            </Fragment>

            {Object.keys(mergedEvaluations)
            .filter((username) => username !== "bbox")
            .map((username) => (
              <Fragment key={username}>
                {PROJECTIONS.map((proj, index) =>
                  renderTableRow(username, proj, index === 0)
                )}
              </Fragment>
            ))}
            </>
          </TableBody>
        </Table>
      </TableContainer>
    </DraggableDialog>
  );
};

export default LabelingStats;
